import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Typography, Grid, withStyles } from '@material-ui/core';
import { TextButton } from '@ds/react-components';
import PageWrapper from 'components/ui/PageWrapper';
import Table from 'components/ui/Table';
import tableMetadata from './tableMetaData';
import SuspensionRuleFilter from './SuspensionRuleFilter';

const styles = (theme) => ({
  row: {
    cursor: 'pointer',
  },
  pageHeader: {
    marginBottom: '20px',
  },
});

const processRuleData = (rules) => {
  return rules.map((r) => {
    const rule = { ...r };
    rule.id = rule.facilityAccountId;
    rule.businessAccount = rule.businessName + ' - ' + rule.businessId;
    rule.formattedFee = rule.fee ?? '-';
    rule.formattedDuration = rule.duration ?? '-';
    rule.admin = (
      <Grid container spacing={1} justify="flex-end">
        <Grid item>
          <TextButton>Edit</TextButton>
        </Grid>
      </Grid>
    );
    return rule;
  });
};

const SuspensionRuleList = (props) => {
  const { getSuspensionRules, appLoading, classes, rules } = props;

  const history = useHistory();

  const [formState, setFormState] = useState({
    currentPage: 0,
    filteredRules: null,
  });

  useEffect(() => {
    const getRules = async () => {
      getSuspensionRules();
    };

    getRules();
  }, [getSuspensionRules]);

  const rulesData =
    formState.filteredRules === null ? rules : formState.filteredRules;

  const suspensionRules = useMemo(() => processRuleData(rulesData), [
    rulesData,
  ]);

  const showNoResults =
    !appLoading &&
    formState.filteredRules !== null &&
    formState.filteredRules.length === 0;

  const formatText = (text) => {
    // Replace hyphen symbols and double spaces if any
    return (text || '').replace(/-/g, ' ').replace(/  +/g, ' ');
  };

  const handleFilter = (data) => {
    const { search } = data;
    let searchResult = rules;

    if (search) {
      searchResult = rules.filter((item) => {
        const businessAccount = formatText(
          item.businessName + ' ' + item.businessId
        ).toLowerCase();
        const formattedSearch = formatText(search).toLowerCase();
        return businessAccount.includes(formattedSearch);
      });
    }

    setFormState((prevState) => ({
      ...prevState,
      currentPage: 0,
      filteredRules: searchResult,
    }));
  };

  const handleRowClick = (event, id) => {
    event.preventDefault();
    history.push(`/SuspensionRules/${id}/Edit`);
  };

  const handlePageClick = (data) => {
    setFormState((prevState) => ({
      ...prevState,
      currentPage: data.selected,
    }));
  };

  return (
    <PageWrapper>
      <Typography
        variant="h2"
        color="inherit"
        className={classes.pageHeader}
        noWrap>
        Suspension Rules
      </Typography>
      {!appLoading && (
        <>
          <SuspensionRuleFilter onSubmit={handleFilter}></SuspensionRuleFilter>
          <Grid container spacing={1} justify="flex-start">
            <Grid item xs={12}>
              <Table
                data={suspensionRules}
                metadata={tableMetadata}
                orderBy={'businessAccount'}
                onRowClick={handleRowClick}
                classes={{ row: classes.row }}
                showNoResults={showNoResults}
                enableSorting={true}
                showFooter={true}
                noBackground={false}
                showEmptyRows={true}
                currentPage={formState.currentPage}
                rowsPerPage={20}
                onPageChange={handlePageClick}
                footerText={
                  <div>
                    <b>{suspensionRules.length}</b> Results Found
                  </div>
                }
              />
            </Grid>
          </Grid>
        </>
      )}
    </PageWrapper>
  );
};

SuspensionRuleList.propTypes = {
  rules: PropTypes.arrayOf(PropTypes.object),
  getSuspensionRules: PropTypes.func.isRequired,
  appLoading: PropTypes.bool.isRequired,
};

SuspensionRuleList.defaultProps = {
  rules: [],
};

export default withStyles(styles)(SuspensionRuleList);
